import { useState } from 'react';
import { Receipt, CheckCircle, Clock, AlertTriangle, XCircle, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { InvoiceDetailCard } from './invoice-detail-card';
import { formatLongDate as formatDate, formatCurrency } from '@/lib/format';

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
}

const statusConfig: Record<string, { label: string; icon: typeof CheckCircle; color: string; bgColor: string }> = {
  draft: { label: 'Draft', icon: Clock, color: 'text-muted-foreground', bgColor: 'bg-muted' },
  sent: { label: 'Pending', icon: Send, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  paid: { label: 'Paid', icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100' },
  overdue: { label: 'Overdue', icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-100' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'text-muted-foreground', bgColor: 'bg-muted' },
};

interface Invoice {
  id: string;
  status: string;
  description: string | null;
  dueDate: string;
  amountCents: number;
  currency: string;
  createdAt: number;
  paidAt?: number | null;
  lineItems?: unknown;
  orgName?: string | null;
}

interface InvoiceListProps {
  invoices: Invoice[];
  activeTab: string;
  onViewAll: () => void;
}

export function InvoiceList({ invoices, activeTab, onViewAll }: InvoiceListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpanded = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoice History</CardTitle>
        <CardDescription>Click an invoice to see details</CardDescription>
      </CardHeader>
      <CardContent>
        {invoices.length === 0 ? (
          <div className="text-center py-8">
            <Receipt className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No invoices found</p>
            {activeTab !== 'all' && (
              <Button variant="link" onClick={onViewAll} className="mt-2">
                View all invoices
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {invoices.map((invoice) => {
              const config = statusConfig[invoice.status] || statusConfig.sent;
              const StatusIcon = config.icon;
              const isOverdue = invoice.status === 'sent' && new Date(invoice.dueDate) < new Date();
              const isExpanded = expandedId === invoice.id;
              const lineItems = invoice.lineItems as LineItem[] || [];

              return (
                <div key={invoice.id} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleExpanded(invoice.id)}
                    className="w-full flex items-center justify-between p-4 bg-muted/40 hover:bg-muted/70 transition text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={cn('p-2 rounded-full flex-shrink-0', config.bgColor)}>
                        <StatusIcon className={cn('w-5 h-5', config.color)} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-foreground truncate">
                          {invoice.description || 'Invoice'}
                        </div>
                        <div className="text-sm text-muted-foreground truncate">
                          {invoice.orgName && <span>{invoice.orgName} &bull; </span>}
                          Due: {formatDate(invoice.dueDate)}
                          {isOverdue && <span className="text-red-500 ml-2">(Overdue)</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-right">
                        <div className="font-bold text-foreground">
                          {formatCurrency(invoice.amountCents, invoice.currency)}
                        </div>
                        <div className={cn('text-xs font-medium', config.color)}>
                          {isOverdue ? 'Overdue' : config.label}
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <InvoiceDetailCard
                      invoice={invoice}
                      lineItems={lineItems}
                      isOverdue={isOverdue}
                      config={config}
                      formatCurrency={formatCurrency}
                      formatDate={formatDate}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
